// Shared dictionaries: words every screen uses (common), and the texts of
// shared/ components and helpers (sharedUi). Feature texts live in
// features/<name>/i18n.ts. Glossary: docs/design/i18n.md.

import type { Role } from '@/api/types'
import { defineMessages } from './index'

export const common = defineMessages({
  ru: {
    save: 'Сохранить',
    cancel: 'Отмена',
    delete: 'Удалить',
    edit: 'Изменить',
    create: 'Создать',
    close: 'Закрыть',
    retry: 'Попробовать снова',
    loading: 'Загрузка…',
    yes: 'да',
    no: 'нет',
    noData: 'нет данных',
    confirm: 'Подтвердить',

    // Units (technical values keep a decimal point in both languages).
    unitBps: 'бит/с',
    unitKbps: 'кбит/с',
    unitMbps: 'Мбит/с',
    unitGbps: 'Гбит/с',
    unitB: 'Б',
    unitKB: 'КБ',
    unitMB: 'МБ',
    unitGB: 'ГБ',
    unitMs: 'мс',
    unitS: 'с',
    unitMin: 'мин',
    unitH: 'ч',
    unitPktS: 'пак/с',
    unitErrS: 'ош/с',
    unitFps: 'к/с',
    unitDbfs: 'дБFS',
    unitCount: 'шт.',
    unitW: 'Вт',

    // Roles of a console user.
    roleViewer: 'Наблюдатель',
    roleOperator: 'Оператор',
    roleEngineer: 'Инженер',
    roleAdmin: 'Администратор',

    // Language switch.
    localeSwitch: 'Язык интерфейса',
    localeRu: 'RU',
    localeEn: 'EN',
  },
  en: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    close: 'Close',
    retry: 'Try again',
    loading: 'Loading…',
    yes: 'yes',
    no: 'no',
    noData: 'no data',
    confirm: 'Confirm',

    unitBps: 'bit/s',
    unitKbps: 'kbit/s',
    unitMbps: 'Mbit/s',
    unitGbps: 'Gbit/s',
    unitB: 'B',
    unitKB: 'KB',
    unitMB: 'MB',
    unitGB: 'GB',
    unitMs: 'ms',
    unitS: 's',
    unitMin: 'min',
    unitH: 'h',
    unitPktS: 'pkt/s',
    unitErrS: 'err/s',
    unitFps: 'fps',
    unitDbfs: 'dBFS',
    unitCount: 'count',
    unitW: 'W',

    roleViewer: 'Viewer',
    roleOperator: 'Operator',
    roleEngineer: 'Engineer',
    roleAdmin: 'Administrator',

    localeSwitch: 'Interface language',
    localeRu: 'RU',
    localeEn: 'EN',
  },
})

/** A user's role in the current language ("Оператор" / "Operator"). */
export function roleLabel(role: Role): string {
  switch (role) {
    case 'viewer':
      return common.t('roleViewer')
    case 'operator':
      return common.t('roleOperator')
    case 'engineer':
      return common.t('roleEngineer')
    case 'admin':
      return common.t('roleAdmin')
    default:
      return role
  }
}

export const sharedUi = defineMessages({
  ru: {
    // Domain states (shared/components/status/tone.ts).
    phaseIdle: 'Не в эфире',
    phaseStarting: 'Запуск',
    phaseRunning: 'Работает',
    phaseDegraded: 'С отклонением',
    phaseBackoff: 'Перезапуск',
    phaseFailed: 'Отказ',
    phaseStopped: 'Остановлен',
    phaseEnded: 'Видео закончилось',
    severityInfo: 'Инфо',
    severityWarning: 'Внимание',
    severityCritical: 'Авария',
    routeOff: 'Выключена',
    routeArmed: 'Готова',
    routeOnAir: 'В эфире',
    routePlainOff: 'Подключено, выключено',
    routePlainArmed: 'Подключено, не в эфире',
    routePlainNothing: 'Ничего не подключено',
    siteActive: 'Активная',
    siteStandby: 'Резерв',
    siteCapture: 'Захват',

    // SRT link quality (srtQuality.ts).
    srtNoData: 'нет данных',
    srtBad: 'плохой канал',
    srtUnstable: 'нестабильный канал',
    srtOk: 'канал в порядке',

    // Form validation (forms/validators.ts, formErrors.ts).
    vRequired: 'обязательное поле',
    vMaxLen: 'не длиннее {max} символов',
    vNoEdgeSpaces: 'без пробелов в начале и в конце',
    vPrintableAscii: 'только печатные ASCII-символы без пробелов',
    vNoCredential: 'не должен содержать «{marker}»: для доступа есть passphrase',
    vLengthRange: 'от {min} до {max} символов',
    vInteger: 'нужно целое число',
    vRange: 'от {min} до {max}',
    vRangeUnit: 'от {min} до {max} {unit}',
    vHostTooLong: 'слишком длинное имя',
    vHost: 'не похоже на имя хоста или IP-адрес',
    validationSummary: 'Сервер не принял изменения: исправьте выделенные поля.',

    // Secret field.
    secretWillClear: 'будет удалён',
    secretUndo: 'Отменить',
    secretKeep: 'Не менять',
    secretIsSet: 'задан',
    secretReplace: 'Заменить',
    secretClear: 'Удалить',

    // Confirm dialog.
    dialogCancel: 'Отмена',

    // Screen stub.
    stubText: 'Экран ещё не собран. Его делает пакет {pkg} из docs/WORKPLAN.md.',

    // Level meter.
    meterLabel: 'Уровень звука',

    // Empty lists and load errors (components/empty).
    loadFailed: 'Не удалось загрузить {what}.',
    loadRetry: 'Повторить',

    // Help hints.
    helpLink: 'Как это работает',
    hintCollapse: 'Свернуть подсказку',
    hintExpand: 'Показать подсказку',

    // Charts.
    chartEmpty: 'Нет данных за период.',
    chartSeries: { one: ', {count} ряд', few: ', {count} ряда', many: ', {count} рядов', other: ', {count} ряда' },
    chartPoints: { one: '{count} точка', few: '{count} точки', many: '{count} точек', other: '{count} точки' },
    chartLocalTime: 'местное время ({offset})',
    chartHideTable: 'Скрыть таблицу',
    chartShowTable: 'Показать таблицу',
    chartCaption: 'Значения графика «{title}» по времени',
    chartTimeColumn: 'Местное время',

    // HLS preview player.
    playerLabel: 'Превью канала',
    playerLoading: 'Загрузка превью…',
    playerRecovering: 'Восстанавливаем превью…',
    playerLost: 'Сессия превью закрыта. Открываем заново…',
    playerUnsupported: 'Этот браузер не умеет воспроизводить HLS.',
    playerUnmute: 'Включить звук',
    playerMute: 'Выключить звук',
    playerRebuilds: 'перезапусков плеера: {count}',

    // Legs table (components/legs).
    legsNone: 'Назначений нет.',
    legsColLeg: 'Назначение',
    legsColState: 'Состояние',
    legsColWhere: 'Куда',
    legsColBitrate: 'Битрейт',
    legsColRttLoss: 'Задержка / потери',
    legsColReconnects: 'Переподключений',
    legsColDropped: 'Сброшено данных',
    legsNeverAll: 'Ни одно назначение ещё не подключалось: {names}.',
    legNotConnected: 'не подключено',
    legNeverConnected: 'ни разу не подключалось',
    legSince: 'с {time}',
    legLastConnected: 'последнее подключение {time}',
  },
  en: {
    phaseIdle: 'Off air',
    phaseStarting: 'Starting',
    phaseRunning: 'Running',
    phaseDegraded: 'Degraded',
    phaseBackoff: 'Restarting',
    phaseFailed: 'Failed',
    phaseStopped: 'Stopped',
    phaseEnded: 'Video ended',
    severityInfo: 'Info',
    severityWarning: 'Warning',
    severityCritical: 'Critical',
    routeOff: 'Off',
    routeArmed: 'Armed',
    routeOnAir: 'On air',
    routePlainOff: 'Connected, switched off',
    routePlainArmed: 'Connected, not on air',
    routePlainNothing: 'Nothing connected',
    siteActive: 'Active',
    siteStandby: 'Standby',
    siteCapture: 'Capture',

    srtNoData: 'no data',
    srtBad: 'bad link',
    srtUnstable: 'unstable link',
    srtOk: 'link OK',

    vRequired: 'required',
    vMaxLen: 'at most {max} characters',
    vNoEdgeSpaces: 'no leading or trailing spaces',
    vPrintableAscii: 'printable ASCII only, no spaces',
    vNoCredential: 'must not contain “{marker}”: use the passphrase for access',
    vLengthRange: '{min} to {max} characters',
    vInteger: 'a whole number is required',
    vRange: '{min} to {max}',
    vRangeUnit: '{min} to {max} {unit}',
    vHostTooLong: 'name too long',
    vHost: 'not a host name or an IP address',
    validationSummary: 'The server rejected the changes: fix the highlighted fields.',

    secretWillClear: 'will be removed',
    secretUndo: 'Undo',
    secretKeep: 'Keep',
    secretIsSet: 'set',
    secretReplace: 'Replace',
    secretClear: 'Remove',

    dialogCancel: 'Cancel',

    stubText: 'This screen is not built yet. Work package {pkg} in docs/WORKPLAN.md builds it.',

    meterLabel: 'Audio level',

    loadFailed: 'Could not load {what}.',
    loadRetry: 'Retry',

    helpLink: 'How it works',
    hintCollapse: 'Hide hint',
    hintExpand: 'Show hint',

    chartEmpty: 'No data for this period.',
    chartSeries: { one: ', {count} series', other: ', {count} series' },
    chartPoints: { one: '{count} point', other: '{count} points' },
    chartLocalTime: 'local time ({offset})',
    chartHideTable: 'Hide table',
    chartShowTable: 'Show table',
    chartCaption: 'Values of the “{title}” chart over time',
    chartTimeColumn: 'Local time',

    playerLabel: 'Channel preview',
    playerLoading: 'Loading preview…',
    playerRecovering: 'Recovering preview…',
    playerLost: 'Preview session closed. Reopening…',
    playerUnsupported: 'This browser cannot play HLS.',
    playerUnmute: 'Unmute',
    playerMute: 'Mute',
    playerRebuilds: 'player restarts: {count}',

    legsNone: 'No destinations.',
    legsColLeg: 'Destination',
    legsColState: 'State',
    legsColWhere: 'Destination',
    legsColBitrate: 'Bitrate',
    legsColRttLoss: 'Delay / loss',
    legsColReconnects: 'Reconnects',
    legsColDropped: 'Data dropped',
    legsNeverAll: 'No destination has connected yet: {names}.',
    legNotConnected: 'not connected',
    legNeverConnected: 'never connected',
    legSince: 'since {time}',
    legLastConnected: 'last connected {time}',
  },
})
